import React from 'react'
import { useController, Control, FieldValues, Path } from 'react-hook-form'
import { TagBox } from 'devextreme-react'

interface IdChipInputProps<T extends FieldValues> {
  name: Path<T>
  control: Control<T>
  placeholder?: string
  label?: string
}

/**
 * Normalizes raw input values into unique positive integers.
 * Supports comma-separated strings (e.g. "10,20,30") and deduplicates.
 */
function normalizeIds(values: unknown[]): number[] {
  const seen = new Set<number>()
  const normalized: number[] = []

  const addId = (value: unknown) => {
    if (value === null || value === undefined || value === '') return
    const parsed = Number(value)
    if (Number.isInteger(parsed) && parsed > 0 && !seen.has(parsed)) {
      seen.add(parsed)
      normalized.push(parsed)
    }
  }

  values.forEach((v) => {
    if (typeof v === 'string' && v.indexOf(',') > -1) {
      v.split(',').forEach((token) => addId(token.trim()))
    } else {
      addId(v)
    }
  })

  return normalized
}

export const IdChipInput = <T extends FieldValues>({
  name,
  control,
  placeholder = 'Type id and press enter',
  label
}: IdChipInputProps<T>) => {
  const {
    field: { value, onChange, ref }
  } = useController<T>({
    name,
    control
  })

  const onValueChanged = (e: { value: unknown[] }) => {
    onChange(normalizeIds(e.value))
  }

  const onCustomItemCreating = (e: { text: string; customItem: unknown }) => {
    const ids = normalizeIds([e.text])
    if (ids.length > 0) {
      const newValues = normalizeIds([...(value || []), ...ids])
      onChange(newValues)
    }
    e.customItem = null // prevent TagBox from adding raw text
  }

  return (
    <div>
      {label && <h4>{label}</h4>}
      <TagBox
        items={[]}
        value={value || []}
        acceptCustomValue={true}
        onValueChanged={onValueChanged}
        onCustomItemCreating={onCustomItemCreating}
        placeholder={placeholder}
        ref={ref}
        searchEnabled={false}
        showDropDownButton={false}
        openOnFieldClick={false}
      />
    </div>
  )
}
